<template>
  <f-box
    :class="[
      `user-card`,
      `user-card--${size}`,
      {
        'is-minimal': minimal,
      }
    ]"
  >
    <div 
      v-if="innerUser"
      class="user-card--infos is-grid is-aligned"
    >
      <div class="is-col-1">
        <f-badge 
          class="user-card--badge"
          :image="logo"
          :placeholder="innerUser.name"
        ></f-badge>
      </div>

      <div class="is-col-4 is-overflow-hidden">
        <p 
          class="user-card--name is-ellipsis"
        >
          <small>
            <b>
              {{ innerUser.firstname }} {{ capitalize(innerUser.lastname) }}
            </b>
          </small>
        </p>
      </div>
      
      <div class="is-col-2 is-overflow-hidden">
        <p 
          class="user-card--email is-ellipsis"
        >
          <small>
            <b>
              {{ innerUser.email }}
            </b>
          </small>
        </p>
      </div>

      <div class="is-col-2 is-overflow-hidden">
        <p 
          class="user-card--phone is-ellipsis"
        >
          <small>
            <b>
              {{ innerUser.phone }}
            </b>
          </small>
        </p>
      </div>

      <div class="is-col-2 is-overflow-hidden">

      </div>

      <div class="is-col-1 is-aligned">
        <f-button
          v-if="canEdit"
          @click="patchUser"
          circle
        >
          <i class="uil uil-save"></i>
        </f-button>

        <f-button
          v-if="canRemove"
          @click="removeUser"
          type="danger"
          circle
        >
          <i 
            :class="[
              `uil`,
              {
                'uil-trash': !waitConfirmation,
                'uil-shield-question': waitConfirmation,
              }
            ]"
          ></i>
        </f-button>
      </div>
    </div>
  </f-box>
</template>

<script>
  import { ref, computed } from 'vue'
  import { useStore } from '@freebe/sdk/store'
  import useLogo from '@composables/users/use-logo'
  import useRoles from '@composables/use-roles'
  import capitalize from '@utils/capitalize'
  import router from '@core/router'

  export default {
    props: {
      user: {
        type: [Object, undefined],
        default: undefined,
      },

      id: {
        type: String,
        default: '',
      },

      selected: {
        type: Boolean,
        default: false,
      },

      selectable: {
        type: Boolean,
        default: false,
      },

      minimal: {
        type: Boolean,
        default: false,
      },

      size: {
        type: String,
        default: 'normal',
      },

      oneShotSelection: {
        type: Boolean,
        default: false,
      },
    },

    setup (props, { emit }) {
      const { checkPermissions } = useRoles()
      const { get, fetch, patch, remove } = useStore('@freebe/users')

      let innerUser = ref(props.user || null)
      let userId = ref(props.id || innerUser.value._id)

      if (!innerUser.value && userId.value) {
        innerUser = computed(() => get(userId.value))

        fetch(userId.value)
      }

      const { logo } = useLogo(userId)

      const waitConfirmation = ref(false)

      const canEdit = computed(() => 
        checkPermissions('freebe/users:patch')
      )

      const canRemove = computed(() => 
        checkPermissions('freebe/users:remove')
      )

      function toggle () {
        emit('toggle', userId.value, !!props.selected)
      }

      function toggleOneShot () {
        if (props.oneShotSelection && props.selectable) {
          toggle()
        }
      }

      async function patchUser () {
        const data = innerUser.value
        const params = {
          query: {
            user: innerUser.value._id
          }
        }
        
        try {
          await patch(innerUser.value._id, data, params)
        } catch (error) {
          console.error('NOTIFICATION ERROR:', error)
        }
      }

      async function removeUser () {
        if (waitConfirmation.value) {
          waitConfirmation.value = false

          await remove(userId.value, {
            query: {
              user: innerUser.value._id
            }
          })

          router.push({
            name: 'users-list',
          })
        } else {
          waitConfirmation.value = true

          setTimeout(() => {
            waitConfirmation.value = false
          }, 2000)
        }
      }

      return {
        innerUser,
        logo,
        toggle,
        toggleOneShot,
        canEdit,
        canRemove,
        waitConfirmation,
        patchUser,
        removeUser,
        capitalize,
      }
    },
  }
</script>

<style lang="scss">
  .user-card {
    width: 100%;
    height: 100%;

    &--name {
      color: $color-secondary;
      margin: 0;
      padding: 0.5rem 0px;
    }

    & > div {
      height: 100%;
      width: 100%;
    }

    &.is-select-one-shot {
      .user-card--badge,
      .user-card--name {
        cursor: pointer;
      }
    }

    &:hover, &.is-selected {
      &.is-select-one-shot {
        // box-shadow: 0 1px 1px 0 rgba(102, 166, 255, 0.3) !important;
      }

      .user-card--one-shot-toggle {
        transform: translate3d(0, 0, 0);
        opacity: 1;
      }
    }

    &--one-shot-toggle {
      transform: translate3d(-20px, 0, 0);
      opacity: 0;
      transition: opacity 0.1s, transform 0.3s $magic-animation;

      &.f-button {
        box-shadow: unset;
        font-size: 1.5rem;
      }
    }

    &--normal {
      min-height: 4rem;
      padding: 0 1rem;
      margin-bottom: 0.33rem;

      &.is-minimal {
        height: 2.8rem;
      }
    }
  }
</style>